import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { FormikHelpers } from 'formik';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import {
  CredentialsForm,
  useCredentialsForm,
  CredentialsFormValues,
} from '../../../forms';
import {
  ScreenLayout,
  AuthHeader,
  FormField,
  SocialProofBanner,
  Spinner,
  LoadingOverlay,
  StyledButton,
} from '../../shared';
import { useFlowGuard } from '../../shared/useFlowGuard';
import {
  messageAtom,
  firstNameAtom,
  phoneAtom,
  emailAtom,
} from '../../../store';
import { api, APIError } from '../../../../../../shared/api';
import { getHostName } from '../../../../../../shared/location';

function formatPhone(value: string): string {
  const digits = Array.from(value)
    .filter((c) => c >= '0' && c <= '9')
    .join('')
    .slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledTitle = styled.h1`
  margin: 1rem 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #232323;
`;

const StyledDescription = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #232323;
`;

const StyledFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledLegal = styled.p`
  margin: 1rem 0 0.75rem;
  font-size: 0.8125rem;
  color: #57534e;

  a {
    color: #57534e;
    text-decoration: underline;
    text-underline-offset: 2px;
    padding: 0.1rem 0.15rem;
    border-radius: 0.2rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f5f5f4;
      text-decoration: none;
    }

    &:focus-visible {
      outline: 2px solid #57534e;
      outline-offset: 2px;
    }
  }
`;

/**
 * TODO: set this to display: none until a sign in flow is spec'd out. Talk to Product if you need clarity.
 */
const StyledSignIn = styled.p`
  margin: 1rem 0;
  font-size: 1rem;
  color: #232323;
  text-align: center;
  display: none;

  button {
    background: none;
    border: none;
    padding: 0;
    color: var(--rg-primary-color, #165e5f);
    font-size: inherit;
    font-family: inherit;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;

const StyledServerError = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: #a62017;
  text-align: center;
`;

function CredentialsFields() {
  const {
    values,
    errors,
    touched,
    status,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    submitForm,
    setFieldValue,
  } = useCredentialsForm();

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('phone', formatPhone(e.target.value));
    },
    [setFieldValue]
  );

  return (
    <>
      <StyledFields>
        <FormField
          name="firstName"
          placeholder="First name"
          value={values.firstName}
          error={errors.firstName}
          touched={touched.firstName}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormField
          name="phone"
          type="tel"
          placeholder="Mobile phone number"
          value={values.phone}
          error={errors.phone}
          touched={touched.phone}
          disabled={isSubmitting}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
        />
        <FormField
          name="email"
          type="email"
          placeholder="Email"
          value={values.email}
          error={errors.email}
          touched={touched.email}
          disabled={isSubmitting}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </StyledFields>

      <StyledLegal>
        By continuing, you agree to{' '}
        <a
          href="https://opiniion.com/legal/terms-of-use/"
          target="_blank"
          rel="noopener noreferrer"
        >
          terms
        </a>{' '}
        and{' '}
        <a
          href="https://opiniion.com/legal/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          privacy policy
        </a>
        .
      </StyledLegal>

      <StyledButton
        type="button"
        disabled={!isValid || isSubmitting}
        onClick={submitForm}
      >
        {isSubmitting ? <Spinner size="1.25rem" color="currentColor" /> : 'Continue'}
      </StyledButton>

      {status && <StyledServerError>{status}</StyledServerError>}

      <StyledSignIn>
        Already have an account? <button type="button">Sign in</button>
      </StyledSignIn>
    </>
  );
}

export function SignUpPage() {
  const ready = useFlowGuard({ atoms: [messageAtom], fallback: '/' });
  const navigate = useNavigate();
  const listing = globalThis.xprops.listing;
  const residents = globalThis.xprops.residents;
  const setFirstName = useSetAtom(firstNameAtom);
  const setPhone = useSetAtom(phoneAtom);
  const setEmail = useSetAtom(emailAtom);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  const createAccount = useMutation({
    mutationFn: async (_values: CredentialsFormValues) => {
      const hostname = getHostName(globalThis.xprops.getParentURL());

      return api.post('/api/consumer/phone_auth/initiate/', {
        hostname,
        phone_number: _values.phone,
        phone_number_country: 'US',
      });
    },
    onSuccess: (_data, values) => {
      setFirstName(values.firstName);
      setPhone(values.phone);
      setEmail(values.email);
      navigate('/auth/verify');
    },
  });

  const handleSubmit = useCallback(
    async (
      values: CredentialsFormValues,
      helpers: FormikHelpers<CredentialsFormValues>
    ) => {
      try {
        await createAccount.mutateAsync(values);
      } catch (error) {
        const errMsg =
          (error as APIError).message ||
          'Something went wrong. Please try again.';

        helpers.setStatus(errMsg);
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [createAccount]
  );

  if (!ready) return null;

  return (
    <ScreenLayout
      header={<AuthHeader onBack={handleBack} onClose={handleClose} />}
      footer={
        <SocialProofBanner
          count={residents.length}
          residents={residents}
          listingName={listing?.name ?? ''}
        />
      }
    >
      {createAccount.isLoading && <LoadingOverlay />}
      <CredentialsForm onSubmit={handleSubmit}>
        <StyledBody>
          <StyledTitle>Enter your information</StyledTitle>
          <StyledDescription>
            Your phone number and email stay private. We use them to securely
            forward resident replies to your phone, so you can connect safely
            without sharing your personal info.
          </StyledDescription>
          <CredentialsFields />
        </StyledBody>
      </CredentialsForm>
    </ScreenLayout>
  );
}
